import {z} from 'zod';
import {MovieTextAnimator} from 'common/src/modules/movie/movieLayers.types';
import { AiTrackingType, TrackingType } from 'common/src/modules/movie/movieLayers.enums';
import {AnimatorRangeBase, AiAnimatorRangeBase} from 'common/src/modules/movie/movie.enums';
import {textAnimatorSchema} from '../adBuilderAgent/adBuilderAgent.schemas';
import {NumberArr} from 'common/src/modules/movie/moviePrimitives.types';
import {isSimpleValue, isKeyframedValue} from 'common/src/modules/movie/utils/isSimpleValue';
import getMatchingEnumValue from 'common/src/utils/typescriptUtils/getMatchingEnumValue';

// Type for the text animator schema
type TextAnimatorSchemaType = z.infer<typeof textAnimatorSchema>;

/**
 * Helper function to extract keyframe values from NumberArr<1>
 * Handles both simple values and keyframed values
 */
function extractKeyframeValues1D(value: NumberArr<1>): Array<{x: number; time: number}> {
  if (!value) {
    return [];
  }
  
  if (isSimpleValue(value)) {
    // Simple value - create a single keyframe at time 0
    return [{x: value.val[0], time: 0}];
  }
  
  if (isKeyframedValue(value)) {
    // Keyframed value - extract all keyframes
    return value.k.map(keyframe => ({
      x: keyframe.s ? keyframe.s[0] : 0,
      time: keyframe.t
    }));
  }
  
  return [];
}

/**
 * Helper function to extract keyframe values from NumberArr<2>
 * Handles both simple values and keyframed values
 */
function extractKeyframeValues2D(value: NumberArr<2>): Array<{x: number; y: number; time: number}> {
  if (!value) {
    return [];
  }
  
  if (isSimpleValue(value)) {
    // Simple value - create a single keyframe at time 0
    return [{x: value.val[0], y: value.val[1], time: 0}];
  }
  
  if (isKeyframedValue(value)) {
    // Keyframed value - extract all keyframes
    return value.k.map(keyframe => ({
      x: keyframe.s ? keyframe.s[0] : 0,
      y: keyframe.s ? keyframe.s[1] : 0,
      time: keyframe.t
    }));
  }
  
  return [];
}

/**
 * Helper function to extract keyframe values from NumberArr<3>
 * Handles both simple values and keyframed values
 */
function extractKeyframeValues3D(value: NumberArr<3>): Array<{x: number; y: number; z: number; time: number}> {
  if (!value) {
    return [];
  }
  
  if (isSimpleValue(value)) {
    // Simple value - create a single keyframe at time 0
    return [{x: value.val[0], y: value.val[1], z: value.val[2], time: 0}];
  }
  
  if (isKeyframedValue(value)) {
    // Keyframed value - extract all keyframes
    return value.k.map(keyframe => ({
      x: keyframe.s ? keyframe.s[0] : 0,
      y: keyframe.s ? keyframe.s[1] : 0,
      z: keyframe.s ? keyframe.s[2] : 0,
      time: keyframe.t
    }));
  }
  
  return [];
}

/**
 * Converts a MovieTextAnimator to the schema format
 * @param animator - The MovieTextAnimator to convert
 * @returns Text animator data in the format expected by textAnimatorSchema
 */
function convertSingleAnimatorToSchema(animator: MovieTextAnimator): TextAnimatorSchemaType {
  const result: TextAnimatorSchemaType = {
    rangeSelector: {
      start: [],
      end: [],
      offset: [],
      unit: 'Percent',
      smoothness: 0,
      basedOn: AiAnimatorRangeBase.Char,
    },
  };

  // Convert opacity
  if (animator.opacity) {
    result.opacity = extractKeyframeValues1D(animator.opacity);
  }

  // Convert color
  if (animator.fillColor) {
    result.color = extractKeyframeValues3D(animator.fillColor);
  }

  // Convert position
  if (animator.position) {
    result.position = extractKeyframeValues2D(animator.position);
  }

  // Convert tracking
  if (animator.tracking) {
    const trackingAmountKeyframes = extractKeyframeValues1D(animator.tracking.trackingAmount);
    result.tracking = {
      amount: trackingAmountKeyframes,
      type: getMatchingEnumValue(TrackingType, AiTrackingType, 
        isSimpleValue(animator.tracking.trackingType) 
          ? animator.tracking.trackingType.val[0] 
          : animator.tracking.trackingType.k[0]?.s?.[0] || TrackingType.Before
      ) || AiTrackingType.Before,
    };
  }

  // Convert range selector
  if (animator.rangeSelectors && animator.rangeSelectors.length > 0) {
    const rangeSelector = animator.rangeSelectors[0];
    
    result.rangeSelector = {
      start: extractKeyframeValues1D(rangeSelector.start),
      end: extractKeyframeValues1D(rangeSelector.end),
      offset: extractKeyframeValues1D(rangeSelector.offset),
      unit: 'Percent', // Always Percent for now
      smoothness: rangeSelector.smoothness,
      basedOn: getMatchingEnumValue(AnimatorRangeBase, AiAnimatorRangeBase, rangeSelector.basedOn) || AiAnimatorRangeBase.Char,
    };
  }

  return result;
}

/**
 * Converts an array of MovieTextAnimator to the schema format
 * @param animators - Array of MovieTextAnimator to convert
 * @returns Array of text animator data in the format expected by textAnimatorSchema
 */
export function convertTextAnimatorsToSchema(animators: MovieTextAnimator[]): TextAnimatorSchemaType[] {
  if (!animators || animators.length === 0) {
    return [];
  }

  return animators.map(convertSingleAnimatorToSchema);
} 